import { useState } from 'react';
import * as R from 'ramda';

import NewWorkOrderIcon from '@atom/components/common/svgIcons/NewWorkOrderIcon';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton } from '@atom/mui';
import colors from '@atom/styles/colors';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';

import ListItemWrapper from '../drawerContentWrappers/ListItemWrapper_v2';
import { useListView } from '../hooks/listViewHook';
import { initialState } from '../hooks/mapDataHook';

import BulkCreateModal from './bulkCreateModal/BulkCreateModal';
import BulkLimitModal from './BulkLimitModal';
import ContentListHeader from './ContentListHeader';
import NoItemsPane from './NoItemsPane_v2';

import classes from './drawerContent.module.css';

const getArrayDepth = value =>
  Array.isArray(value) ? 1 + Math.max(...value.map(getArrayDepth)) : 0;
const sortAssets = R.sortBy(
  R.pipe(R.path(['location', 'coordinates']), getArrayDepth),
);

const DrawerContentList = () => {
  const [data = initialState] = useListView();
  const [{ id: ids }, { updateSearchParams }] = useNavigation({
    id: SearchParamType.ARRAY,
  });
  const [workOpen, setWorkOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState<boolean>(false);
  const [limitModalOpen, setLimitModalOpen] = useState<boolean>(false);

  const toggleWork = () => setWorkOpen(!workOpen);
  const toggleUser = () => setUserOpen(!userOpen);
  const toggleAsset = () => setAssetOpen(!assetOpen);

  const { clusters = [], workOrders = [], users = [], assets = [] } = data;
  const length = workOrders.length + users.length + assets.length;
  const sortedAssets = sortAssets(assets);

  const closeCreateModal = () => setCreateModalOpen(false);
  const closeLimitModal = () => setLimitModalOpen(false);

  const preferences = usePreferences();
  const enableBulkImplementation = R.pathOr(
    false,
    ['workOrders', 'createBulkWorkFromMap'],
    preferences,
  );

  const showBulkCreateButton = hasRolePermissions(ROLE_SETS.MANAGER);

  const createWorkInBulk = () => {
    if (assets.length > 5000) {
      setLimitModalOpen(true);
    } else {
      setCreateModalOpen(true);
    }
  };

  return (
    <div className={classes.sidepaneListContainer}>
      {enableBulkImplementation && length === 0 && (
        <div className={classes.drawerContentList}>
          <NoItemsPane
            icon="location_off"
            text="No items to show"
            subtext={
              clusters.length > 0
                ? 'Please zoom closer on the map to view items'
                : null
            }
          />
        </div>
      )}
      {!enableBulkImplementation && !ids && (
        <div className={classes.listHeader}>{length} items</div>
      )}
      {ids && (
        <div className={classes.listHeader}>
          Identify results
          <div className={classes.closeButton}>
            <IconButton
              onClick={() => {
                updateSearchParams({ id: null });
              }}
            >
              <Icon>close</Icon>
            </IconButton>
          </div>
        </div>
      )}
      <div className={classes.drawerContentList}>
        {enableBulkImplementation && (
          <ContentListHeader
            count={workOrders.length}
            label="Work Order"
            open={workOpen}
            toggle={toggleWork}
          />
        )}
        {(workOpen || !enableBulkImplementation) &&
          workOrders
            .filter(({ id }) => !ids || R.includes(id, ids))
            .map(workOrder => (
              <ListItemWrapper
                key={workOrder.id}
                row={workOrder}
                param={'workOrder'}
              />
            ))}
        {enableBulkImplementation && (
          <ContentListHeader
            count={users.length}
            label="Team Member"
            open={userOpen}
            toggle={toggleUser}
          />
        )}
        {(userOpen || !enableBulkImplementation) &&
          users
            .filter(({ id }) => !ids || R.includes(id, ids))
            .map(user => (
              <ListItemWrapper key={user.id} row={user} param={'user'} />
            ))}
        {enableBulkImplementation && (
          <ContentListHeader
            actionIcon={<NewWorkOrderIcon color={colors.neutral.gray} />}
            showActionButton={showBulkCreateButton}
            actionOnClick={createWorkInBulk}
            actionTooltip="Create Work in Bulk"
            count={sortedAssets.length}
            label="Inventory Item"
            open={assetOpen}
            toggle={toggleAsset}
          />
        )}
        {(assetOpen || !enableBulkImplementation) &&
          sortedAssets
            .filter(({ id }) => !ids || R.includes(id, ids))
            .map(asset => (
              <ListItemWrapper key={asset.id} row={asset} param={'asset'} />
            ))}
      </div>
      <BulkCreateModal
        assets={assets}
        open={createModalOpen}
        onClose={closeCreateModal}
      />
      <BulkLimitModal open={limitModalOpen} onConfirm={closeLimitModal} />
    </div>
  );
};

export default DrawerContentList;
